import { Button, useDisclosure } from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { WizardVehicleAdditionalData as Step4FormValues } from 'BrokerTsLib/src/dtos'
import { InitialPolicyHolderAdditionalData } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import type { AdditionalInfoValidationSchemaContext } from 'BrokerTsLib/src/validation'
import { AdditionalInfoVehicleValidationSchema, AdditionalPolicyHolderValidationSchema } from 'BrokerTsLib/src/validation'
import _ from 'lodash'
import { useContext, useEffect, useState } from 'react'
import * as yup from 'yup'

import { Step4Context } from '@/pages/carkulka/wizard/4'

import ErrorSummary from '../../components/ErrorSummary'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { MainContent } from '../../components/MainContent'
import { useApp } from '../../hooks/useApp'
import { useGetStep4 } from '../../hooks/useWizard'
import { Save } from '../../icons'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { AdditionalInsureeInfo } from '../AdditionalInsureeInfo'
import { AdditionalVehicleInfo } from '../AdditionalVehicleInfo'
import { FormButtons } from '../FormButtons'

interface Step4FormProps {
  onSubmit: Function
}

const schema = (context?: AdditionalInfoValidationSchemaContext) => yup.object().shape(
  {
    vehicle: AdditionalInfoVehicleValidationSchema(context),
    policyHolder: AdditionalPolicyHolderValidationSchema(context)
  }
)

export const Step4Form = ({ onSubmit }: Step4FormProps) => {
  const { insuranceParams, insuranceParamsId } = useContext(insuranceParamsContext)
  const { offer } = useContext(Step4Context)
  const isContract = !!insuranceParams?.generalParams.contractNumber
  const { app } = useApp()

  const { isLoading, isFetching, data: axiosResponse, refetch } = useGetStep4(insuranceParamsId ?? '')

  const methods = useForm<Step4FormValues>({
    defaultValues: {},
    resolver: yupResolver(schema({ offer, extra: app === 'carkulka-extra' })) as any,
  })
  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors },
    setError
  } = methods
  
  useEffect(() => {
    if (axiosResponse?.data) reset({
      ...axiosResponse.data as Step4FormValues,
      policyHolder: {
        ...InitialPolicyHolderAdditionalData,
        ...axiosResponse.data.policyHolder,
      },
    })
  }, [axiosResponse, reset])

  const data = axiosResponse?.data as Step4FormValues

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  const submitForm = handleSubmit(data => {
    const values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak muze dochazet k casovemu posunu, coz znamena na serveru predchozi den
    values.vehicle.inspectionDate = formatDate(values.vehicle.inspectionDate)
    return onSubmit(values)
      .then(() => {
        if (values.nextPage == 'current') {
          savedFormAlertOnOpen()
          refetch()
        }
      })
      .catch((err: any) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          {app !== 'carkulka-extra' &&
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Doplňující informace o vozidle</FormSectionTitle>

                <FormSectionDescription>
                  Doplňte informace o vozidle, jako je bližší specifikace nebo jeho poškození.
                </FormSectionDescription>
              </FormSectionHeader>

              {!data?.vehicle && (isLoading || isFetching) && <Loading large={true} />}
              {data?.vehicle &&
                <FormSectionBody>
                  <AdditionalVehicleInfo />
                </FormSectionBody>
              }
            </FormSection>
          }

          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Doplňující informace o pojistníkovi</FormSectionTitle>

              <FormSectionDescription>
                Doplňte informace o pojistníkovi a dalších osobách pro sjednání pojištění.
              </FormSectionDescription>
            </FormSectionHeader>

            {!data?.policyHolder && (isLoading || isFetching) && <Loading large={true} />}
            {data?.policyHolder &&
              <FormSectionBody>
                {data?.policyHolder && <AdditionalInsureeInfo />}
              </FormSectionBody>
            }
          </FormSection>

          <ErrorSummary errors={errors} />

          {!isContract &&
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}>
                    Uložit
                  </Button>
                  <Button
                    colorScheme="primary"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingNext}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Save />
                    Uložit a pokračovat na shrnutí
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    header='Uloženo'
                    body={'Údaje byly úspěšně uloženy'}
                  />
                </>
              }
            />
          }
        </MainContent>
      </form>
    </FormProvider>
  )
}
